// (droneSystem imports only ./types, so this is not a cycle.)
import { droneVolumeM3 } from "./droneSystem";

// 9-resource catalog (design doc Section 16.2). Resource tier is intrinsic
// to the resource name -- it does NOT vary by the system it's mined in.
// A system's own Danger Tier only governs which resources that system
// stocks (16.22), not the CR/m3 rate of a resource once you're mining it.

export type ResourceTier = 0 | 1 | 2 | 3 | 4;
export type Richness = "Poor" | "Standard" | "Rich" | "Exceptional";

export const RESOURCE_TIER: Record<string, ResourceTier> = {
  "Ferrite Ore": 0,
  "Aluminum Ore": 1,
  "Frozen Volatiles": 1,
  "Titanium Ore": 2,
  "Silicate Crystal": 2,
  "Duranium Alloy": 3,
  "Volatile Crystal": 3,
  "Neutronium Ore": 4,
  "Xenite Alloy": 4
};

// Base CR/m3 by tier, anchored at 5 CR/m3 for Tier 1, reusing the same
// 2.2x/tier reward-multiplier curve as everything else in 16.1 (16.2).
export const BASE_CR_PER_M3: Record<ResourceTier, number> = {
  0: 2.5,
  1: 5.0,
  2: 11.0,
  3: 24.2,
  4: 53.25
};

// Node yield-quantity multiplier by richness (16.2).
export const RICHNESS_MULTIPLIER: Record<Richness, number> = {
  Poor: 0.7,
  Standard: 1.0,
  Rich: 1.5,
  Exceptional: 2.5
};

export function resourceCrPerM3(resource: string): number {
  const tier = RESOURCE_TIER[resource];
  if (tier === undefined) {
    throw new Error(`Unknown resource: "${resource}"`);
  }
  return BASE_CR_PER_M3[tier];
}

// Per-unit volume by tier (16.40, EVE-derived) -- replaces the earlier
// uniform "1 unit = 1 m3" placeholder. Anchored at EVE Online's own real
// endpoints for its ore table: Veldspar (the most common ore) is 0.1
// m3/unit, Arkonor (a rare, high-value ore) is 16 m3/unit -- a real ~160x
// spread this project already anchors combat/mining numbers to
// (eve-npc-reference-table.md). Mapped across our 5 resource tiers rather
// than EVE's ~16 ore types: common Tier 0 ore is tiny per unit (a stack of
// it looks abundant), rare Tier 4 ore is bulky per unit (a stack looks
// precious and heavy) -- exactly EVE's own convention, not the reverse.
//
// This does NOT change any of the m3-native systems that were already
// balanced around real volume: extraction rate (m3/s, 16.25), asteroid
// node pools (m3, 16.25), mission quotas (quotaM3) and rewards
// (quotaM3 * resourceCrPerM3, miningMissions.ts) all keep working in pure
// m3 exactly as before. Only how a mined/bought/sold STASH of a resource
// is counted -- in units, with volume derived -- actually changes.
export const VOLUME_PER_UNIT_M3: Record<ResourceTier, number> = {
  0: 0.1,
  1: 0.5,
  2: 2,
  3: 6,
  4: 16
};

// 1 for anything that isn't one of the 9 real resources (Market goods,
// wreck salvage/artifacts) -- those keep the old 1 unit = 1 m3 convention,
// deliberately not extended to this pass (see 16.40's own note on scope).
// **Drones are the one exception (16.74):** they carry a real per-tier
// volume, because a bay's worth of them is a genuine cargo commitment and
// pretending a Capital drone occupies 1 m3 would make the trade-off
// meaningless.
export function volumePerUnitM3(itemName: string): number {
  const droneVolume = droneVolumeM3(itemName);
  if (droneVolume !== undefined) return droneVolume;
  const tier = RESOURCE_TIER[itemName];
  return tier === undefined ? 1 : VOLUME_PER_UNIT_M3[tier];
}

// Real CR/unit for a resource -- CR/m3 times how much volume one unit
// actually takes, so a caller pricing a STORED QUANTITY (units) never has
// to know about volume separately (marketPricePerUnit, marketItems.ts,
// is the one place this is actually used).
export function resourceCrPerUnit(resource: string): number {
  return resourceCrPerM3(resource) * volumePerUnitM3(resource);
}
